function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useContext, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { XYChart, Axis, DataContext, Tooltip, TooltipProvider, TooltipContext, LineSeries, ThemeContext } from '@visx/xychart';
import getScaledValueFactory from '@visx/xychart/esm/utils/getScaledValueFactory.js';
import { Line, LinePath } from '@visx/shape';
import { ParentSize } from '@visx/responsive';
import { Attributes, Formatters, Normalizers } from '@leverege/ui-attributes';
import { Selection } from '@leverege/ui-redux';
import { ModelFactory } from '@leverege/ui-plugin';
import { Config } from '@leverege/plugin';
import { ns } from '@leverege/i18n';
import { Content, Pane } from '@leverege/ui-elements';
import { XYChartModel, AttributeCoordinateModel } from "../models/index.js";
import { buildCustomChartTheme, getDefaultIconForSeriesType, getTypeForSeries, seriesDataIsEmpty } from "./Util.js";
import { GlyphRendererFactory, SeriesRendererContainerFactory, SeriesRendererFactory, GraphBackgroundFactory } from "./Factory.js";
import { getVisxScaleModelForModel } from "../models/scale/Util.js";
import RolloverSelectionPointer from "./RolloverSelectionPointer.js";
import S from './XYChartRenderer.css';
import { useSeriesData } from "../hooks/index.js";
import Thresholdizers from "../attribute/Thresholdizers.js";
const {
  t
} = ns('ui-attributes-graphs.viewers.XYChartRenderer');
const DEFAULT_MARGIN = {
  top: 10,
  right: 50,
  bottom: 60,
  left: 75
};
const BASE_X_TICK_WIDTH = 70;
const BASE_Y_TICK_HEIGHT = 15;
class XYChartRenderer extends React.PureComponent {
  static contextType = TooltipContext;
  constructor(props) {
    super(props);
    this.theme = buildCustomChartTheme();
  }

  // Can't prevent cyclic update so disabling for now
  // componentDidUpdate( prevProps ) {
  //   const { rollover } = this.props
  //   const { rollover : pRollover } = prevProps
  //   const { tooltipData } = this.context

  //   if ( rollover !== pRollover ) {
  //     this.showTooltip( rollover?.items[0] )
  //     return
  //   }

  //   if ( tooltipData?.nearestDatum?.datum && tooltipData.nearestDatum.datum !== rollover?.items?.[0] ) {
  //     GlobalState.dispatch( Selection.set( 'rollover', tooltipData.nearestDatum.datum ) )
  //   } else if ( tooltipData?.nearestDatum?.datum == null && !Selection.isEmpty( rollover ) ) {
  //     GlobalState.dispatch( Selection.clear( 'rollover' ) )
  //   }
  // }

  showTooltip(item) {
    const {
      data
    } = this.props;
    const {
      showTooltip,
      hideTooltip
    } = this.context;
    if (item) {
      let key, index;
      const entries = Object.entries(data);
      for (let i = 0; i < entries.length; i++) {
        const [k, value] = entries[i];
        for (let j = 0; j < value.length; j++) {
          const obj = value[j];
          if (obj.id === item.id) {
            key = k;
            index = j;
            break;
          }
        }
        if (key) {
          break;
        }
      }
      showTooltip({
        index,
        datum: item,
        key
      });
    } else {
      hideTooltip();
    }
  }
  getX = obj => {
    if (!obj) {
      return null;
    }
    const {
      model
    } = this.props;
    const xCoordinate = XYChartModel.getXCoordinate(model);
    return Attributes.get(xCoordinate.attrName, obj.type, obj.data);
  };
  getY = obj => {
    if (!obj) {
      return null;
    }
    const {
      model
    } = this.props;
    const yCoordinate = XYChartModel.getYCoordinate(model);
    return Attributes.get(yCoordinate.attrName, obj.type, obj.data);
  };
  getAccessor = (dimension, seriesKey) => {
    return datum => {
      if (datum == null) {
        return NaN;
      }
      const obj = datum[seriesKey];
      if (obj == null) {
        return NaN;
      }
      if (dimension === 'x') {
        const x = this.getXRaw(obj);
        return x;
      }
      const y = this.getYRaw(obj);
      return y;
    };
  };
  getXRaw = obj => {
    const {
      model
    } = this.props;
    const xCoordinate = XYChartModel.getXCoordinate(model);
    const val = this.getX(obj);
    let opts;
    if (xCoordinate?.formatter?.unit) {
      opts = {
        toUnit: xCoordinate.formatter.unit
      };
    }
    return Normalizers.normalize(xCoordinate.attrName, val, opts);
  };
  getYRaw = obj => {
    const {
      model
    } = this.props;
    const yCoordinate = XYChartModel.getYCoordinate(model);
    const val = this.getY(obj);
    let opts;
    if (yCoordinate?.formatter?.unit) {
      opts = {
        toUnit: yCoordinate.formatter.unit
      };
    }
    return Normalizers.normalize(yCoordinate.attrName, val, opts);
  };
  xTickFormat = d => {
    const {
      model
    } = this.props;
    const xCoordinate = XYChartModel.getXCoordinate(model);
    let opts;
    if (xCoordinate?.formatter?.unit) {
      opts = {
        toUnit: xCoordinate.formatter.unit
      };
    }
    const denormalized = Normalizers.denormalize(xCoordinate.attrName, d, opts);
    return Formatters.format(denormalized, xCoordinate.formatter).join(' ');
  };
  yTickFormat = (d, ...rest) => {
    const {
      model
    } = this.props;
    const yCoordinate = XYChartModel.getYCoordinate(model);
    let opts;
    if (yCoordinate?.formatter?.unit) {
      opts = {
        toUnit: yCoordinate.formatter.unit
      };
    }
    const denormalized = Normalizers.denormalize(yCoordinate.attrName, d, opts);
    return Formatters.format(denormalized, yCoordinate.formatter).join(' ');
  };
  getMargin() {
    const {
      model
    } = this.props;
    const margin = XYChartModel.getMargin(model);
    return Object.keys(DEFAULT_MARGIN).reduce((prev, curr) => {
      if (margin[curr] == null) {
        prev[curr] = DEFAULT_MARGIN[curr];
      } else {
        prev[curr] = margin[curr];
      }
      return prev;
    }, {});
  }
  renderTooltipGlyph = glyphProps => {
    const {
      model,
      context
    } = this.props;
    const glyphModel = XYChartModel.getGlyph(model);
    return GlyphRendererFactory.create(glyphModel, {
      ...glyphProps,
      context
    });
  };
  getModelOptions() {
    const {
      model,
      width: pWidth,
      height: pHeight
    } = this.props;
    const seriesType = XYChartModel.getSeriesType(model);
    const width = XYChartModel.getWidth(model) || pWidth;
    const height = XYChartModel.getHeight(model) || pHeight;
    const margin = this.getMargin();
    const xLabelOffset = XYChartModel.getXLabelOffset(model) || undefined;
    const yLabelOffset = XYChartModel.getYLabelOffset(model) || undefined;
    const xCoordinate = XYChartModel.getXCoordinate(model);
    const yCoordinate = XYChartModel.getYCoordinate(model);
    const threshold = XYChartModel.getThreshold(model);
    const thresholds = XYChartModel.getThresholds(model);
    const rolloverVisible = XYChartModel.getRolloverVisible(model);
    const orientation = XYChartModel.getOrientation(model) || 'vertical';
    const xAxisPosition = XYChartModel.getXAxisPosition(model) || 'bottom';
    const yAxisPosition = XYChartModel.getYAxisPosition(model) || 'left';
    const xAxisLabel = xCoordinate ? AttributeCoordinateModel.getLabel(xCoordinate) : null;
    const yAxisLabel = yCoordinate ? AttributeCoordinateModel.getLabel(yCoordinate) : null;
    const xScale = xCoordinate ? getVisxScaleModelForModel(AttributeCoordinateModel.getScale(xCoordinate)) : null;
    const yScale = yCoordinate ? getVisxScaleModelForModel(AttributeCoordinateModel.getScale(yCoordinate)) : null;
    const xAttributeName = xCoordinate?.attrName;
    const yAttributeName = yCoordinate?.attrName;
    const snapTooltipToDatumX = XYChartModel.getSnapToDatumX(model);
    const snapTooltipToDatumY = XYChartModel.getSnapToDatumY(model);
    const showHorizontalCrosshair = XYChartModel.getShowHorizontalCrosshair(model);
    const showVerticalCrosshair = XYChartModel.getShowVerticalCrosshair(model);
    const background = XYChartModel.getBackground(model);
    const xTickIntegers = xScale?.integerTicks;
    const yTickIntegers = yScale?.integerTicks;
    const xTickDensity = xScale?.tickDensity;
    const yTickDensity = yScale?.tickDensity;
    return {
      seriesType,
      width,
      height,
      margin,
      xLabelOffset: orientation === 'vertical' ? xLabelOffset : yLabelOffset,
      yLabelOffset: orientation === 'vertical' ? yLabelOffset : xLabelOffset,
      xCoordinate,
      yCoordinate,
      threshold,
      thresholds,
      rolloverVisible,
      xAxisLabel,
      yAxisLabel,
      xScale: orientation === 'vertical' ? xScale : yScale,
      yScale: orientation === 'vertical' ? yScale : xScale,
      xAttributeName,
      yAttributeName,
      snapTooltipToDatumX,
      snapTooltipToDatumY,
      showHorizontalCrosshair,
      showVerticalCrosshair,
      background,
      xTickIntegers,
      yTickIntegers,
      xTickDensity,
      yTickDensity,
      orientation,
      xAxisPosition,
      yAxisPosition
    };
  }
  renderRollover = props => {
    const {
      pointer
    } = this.props;
    return pointer.renderRollover(props);
  };
  render() {
    const {
      data,
      pointer,
      context,
      noDataMessage,
      loading,
      objectType
    } = this.props;
    const {
      seriesType,
      width,
      height,
      margin,
      xLabelOffset,
      yLabelOffset,
      xCoordinate,
      yCoordinate,
      threshold,
      thresholds,
      rolloverVisible,
      xAxisLabel,
      yAxisLabel,
      xScale,
      yScale,
      xAttributeName,
      yAttributeName,
      snapTooltipToDatumX,
      snapTooltipToDatumY,
      showHorizontalCrosshair,
      showVerticalCrosshair,
      background,
      xTickIntegers,
      yTickIntegers,
      xTickDensity,
      yTickDensity,
      orientation,
      xAxisPosition,
      yAxisPosition
    } = this.getModelOptions();
    if (!xCoordinate || !yCoordinate) {
      return null;
    }
    const dataIsEmpty = seriesDataIsEmpty(data);
    if (loading && dataIsEmpty) {
      return /*#__PURE__*/React.createElement(Content, {
        variant: "xyChartLoadingContent"
      }, /*#__PURE__*/React.createElement(Content.Area, {
        layout: "flex:center"
      }, t('loading')));
    }
    if (dataIsEmpty) {
      return /*#__PURE__*/React.createElement(Content, {
        variant: "xyChartNoDataContent"
      }, /*#__PURE__*/React.createElement(Content.Area, {
        layout: "flex:center"
      }, /*#__PURE__*/React.createElement(NoDataView, {
        message: noDataMessage,
        seriesType: seriesType
      })));
    }
    return /*#__PURE__*/React.createElement(XYChart, {
      horizontal: orientation === 'horizontal',
      key: `${xAttributeName}-${yAttributeName}`,
      theme: this.theme,
      xScale: xScale,
      yScale: yScale,
      height: height,
      margin: margin,
      width: width
    }, GraphBackgroundFactory.create(background), thresholds && thresholds.map((threshold, index) => /*#__PURE__*/React.createElement(EnhancedThreshold, {
      key: `threshold-${threshold.id}`,
      threshold: threshold,
      context: context,
      xAccessor: this.getXRaw,
      data: data,
      index: index
    })), /*#__PURE__*/React.createElement(SeriesRenderers, {
      orientation: orientation,
      objectType: objectType,
      seriesType: seriesType,
      data: data,
      theme: this.theme,
      getAccessor: this.getAccessor,
      xAccessor: this.getXRaw,
      context: context
    }), /*#__PURE__*/React.createElement(Axes, {
      orientation: orientation,
      xAxisPosition: xAxisPosition,
      yAxisPosition: yAxisPosition,
      xAxisLabel: xAxisLabel,
      yAxisLabel: yAxisLabel,
      xTickFormat: this.xTickFormat,
      yTickFormat: this.yTickFormat,
      xLabelOffset: xLabelOffset,
      yLabelOffset: yLabelOffset,
      xTickIntegers: xTickIntegers,
      yTickIntegers: yTickIntegers,
      xTickDensity: xTickDensity,
      yTickDensity: yTickDensity
    }), threshold && /*#__PURE__*/React.createElement(Threshold, {
      data: data,
      threshold: threshold
    }), rolloverVisible && pointer && /*#__PURE__*/React.createElement(Tooltip, {
      unstyled: true,
      applyPositionStyle: true,
      snapTooltipToDatumX: snapTooltipToDatumX,
      snapTooltipToDatumY: snapTooltipToDatumY,
      showHorizontalCrosshair: showHorizontalCrosshair,
      showVerticalCrosshair: showVerticalCrosshair,
      renderGlyph: this.renderTooltipGlyph,
      showDatumGlyph: true,
      renderTooltip: this.renderRollover
    }));
  }
}
XYChartRenderer.propTypes = {
  data: PropTypes.array,
  model: PropTypes.object.isRequired,
  objectType: PropTypes.string.isRequired,
  context: PropTypes.object,
  pointer: PropTypes.instanceOf(RolloverSelectionPointer),
  noDataMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loading: PropTypes.bool
};
XYChartRenderer.defaultProps = {
  context: {},
  pointer: undefined,
  get noDataMessage() {
    return t('noDataMessage');
  },
  loading: false
};
function Axes({
  xAxisLabel,
  yAxisLabel,
  xLabelOffset,
  yLabelOffset,
  xTickFormat,
  yTickFormat,
  xTickIntegers,
  yTickIntegers,
  xTickDensity,
  yTickDensity,
  orientation,
  xAxisPosition,
  yAxisPosition
}) {
  const cxt = useContext(DataContext);
  const {
    xScale,
    yScale,
    innerWidth,
    innerHeight,
    horizontal
  } = cxt;
  const {
    xTickValues,
    yTickValues,
    xNumTicks,
    yNumTicks
  } = useMemo(() => {
    const ret = {};
    const baseNumXTicks = horizontal ? Math.round(innerHeight / BASE_Y_TICK_HEIGHT) : Math.round(innerWidth / BASE_X_TICK_WIDTH);
    const baseYNumTicks = horizontal ? Math.round(innerWidth / BASE_X_TICK_WIDTH) : Math.round(innerHeight / BASE_Y_TICK_HEIGHT);
    ret.xNumTicks = xTickDensity == null ? null : Math.round(baseNumXTicks * (xTickDensity / 100));
    ret.yNumTicks = yTickDensity == null ? null : Math.round(baseYNumTicks * (yTickDensity / 100));
    if (xTickIntegers) {
      const useScale = horizontal ? yScale : xScale;
      ret.xTickValues = useScale?.ticks ? useScale.ticks().filter(tick => Number.isInteger(tick)) : undefined;
    }
    if (yTickIntegers) {
      const useScale = horizontal ? xScale : yScale;
      ret.yTickValues = useScale?.ticks ? useScale.ticks().filter(tick => Number.isInteger(tick)) : undefined;
    }
    return ret;
  }, [xScale, yScale, xTickIntegers, yTickIntegers, xTickDensity, yTickDensity, innerWidth, innerHeight]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Axis, {
    label: xAxisLabel,
    labelOffset: xLabelOffset,
    orientation: orientation === 'horizontal' ? yAxisPosition : xAxisPosition,
    tickFormat: xTickFormat,
    numTicks: xNumTicks,
    tickValues: xTickValues
  }), /*#__PURE__*/React.createElement(Axis, {
    label: yAxisLabel,
    labelOffset: yLabelOffset,
    orientation: orientation === 'horizontal' ? xAxisPosition : yAxisPosition,
    tickFormat: yTickFormat,
    numTicks: yNumTicks,
    tickValues: yTickValues
  }));
}
function NoDataView({
  message,
  seriesType
}) {
  const type = getTypeForSeries(seriesType);
  let noDataIcon = Config.get('XYChart', `noDataIcon${type ? `.${type}` : ''}`, getDefaultIconForSeriesType(type));
  if (noDataIcon != null && typeof noDataIcon !== 'string') {
    // assume its a component class/function
    const Clazz = noDataIcon;
    noDataIcon = /*#__PURE__*/React.createElement(Clazz, null);
  } else {
    const noDataDimensions = Config.get('XYChart', 'noDataIconDimensions', {
      height: 224,
      width: 224
    });
    noDataIcon = /*#__PURE__*/React.createElement("img", {
      src: noDataIcon,
      alt: t('altNoData'),
      height: noDataDimensions.height,
      width: noDataDimensions.width
    });
  }
  return /*#__PURE__*/React.createElement(Pane, {
    variant: "noHistoryView|noDataView",
    className: classnames(S.win, S.noDataContainer),
    layout: "flex:rowL"
  }, noDataIcon, message);
}
function Threshold({
  data,
  threshold
}) {
  const {
    yScale,
    width,
    innerWidth,
    theme
  } = useContext(DataContext);
  let seriesData;
  if (typeof threshold !== 'number') {
    return null;
  }
  if (Array.isArray(data)) {
    seriesData = data;
  } else if (typeof data === 'object') {
    const firstKey = Object.keys(data)[0];
    seriesData = data[firstKey];
  }
  if (!seriesData || !yScale || threshold == null) {
    return null;
  }
  const offset = (width - innerWidth) / 2;
  return /*#__PURE__*/React.createElement(Line, {
    z: 1,
    from: {
      x: offset,
      y: yScale(threshold)
    },
    to: {
      x: innerWidth + offset,
      y: yScale(threshold)
    },
    strokeWidth: 2,
    stroke: theme.threshold?.stroke,
    fill: theme.threshold?.fill
  });
}
function EnhancedThreshold({
  threshold,
  context,
  xAccessor,
  data,
  index
}) {
  const {
    value,
    color,
    opacity = 1,
    width = 1
  } = threshold;
  const {
    xScale,
    yScale
  } = useContext(DataContext);
  const yAccessor = useCallback(item => {
    return Thresholdizers.getThreshold(value, item, context) ?? 0;
  }, [value, context]);
  const getScaledX = useMemo(() => {
    if (!xScale || !xAccessor) {
      return null;
    }
    return getScaledValueFactory(xScale, xAccessor);
  }, [xScale, xAccessor]);
  const getScaledY = useMemo(() => {
    if (!yScale) {
      return null;
    }
    return getScaledValueFactory(yScale, yAccessor);
  }, [yScale, yAccessor]);
  const isDefined = useCallback(d => isValidNumber(xScale(xAccessor(d))) && isValidNumber(yScale(yAccessor(d))), [xScale, xAccessor, yScale, yAccessor]);
  if (!getScaledX || !getScaledY) {
    return null;
  }
  return /*#__PURE__*/React.createElement(LinePath, {
    stroke: color,
    strokeWidth: width,
    strokeOpacity: opacity,
    x: getScaledX,
    y: getScaledY,
    defined: isDefined,
    data: data
  });
}
function SeriesRenderers({
  theme,
  orientation,
  data: dataProps,
  getAccessor,
  xAccessor,
  seriesType,
  objectType
}) {
  const {
    data,
    seriesKeys
  } = useSeriesData(dataProps, {
    xAccessor,
    objectType
  });
  const {
    colors: dColors
  } = useContext(ThemeContext);
  const colors = theme?.colors || dColors;
  if (seriesType && Array.isArray(data)) {
    const Container = SeriesRendererContainerFactory.get(seriesType);
    const Model = ModelFactory.get(seriesType);
    const seriesProps = Model?.getSeriesProps?.(seriesType);
    const containerProps = Model?.getContainerProps?.(seriesType);
    return /*#__PURE__*/React.createElement(Container, containerProps, seriesKeys.map((key, index) => {
      const xAcc = orientation === 'vertical' ? obj => obj?.x : getAccessor('y', key);
      const yAcc = orientation === 'vertical' ? getAccessor('y', key) : obj => obj?.x;
      const renderer = SeriesRendererFactory.create(seriesType, {
        ...seriesProps,
        key,
        dataKey: key,
        data: seriesType?.type === 'graph.series.bar' ? data : data.filter(datum => datum[key] != null),
        xAccessor: xAcc,
        yAccessor: yAcc,
        stroke: colors[index % colors.length]
      });
      return renderer;
    }));
  }
  return null;
}
function WrappedChart(props) {
  return /*#__PURE__*/React.createElement(TooltipProvider, null, /*#__PURE__*/React.createElement(ParentSize, {
    parentSizeStyles: {
      overflow: 'hidden',
      width: '100%',
      height: '100%'
    }
  }, ({
    width,
    height
  }) => /*#__PURE__*/React.createElement(XYChartRenderer, _extends({}, props, {
    width: width,
    height: height
  }))));
}
function isValidNumber(num) {
  return num != null && typeof num === 'number' && !Number.isNaN(num) && Number.isFinite(num);
}
WrappedChart.propTypes = XYChartRenderer.propTypes;
WrappedChart.defaultProps = XYChartRenderer.defaultProps;
export default connect(state => {
  return {
    rollover: Selection.get(state, 'rollover')
  };
})(WrappedChart);